/**
 * @file Utility functions
 * @description
 * Shared helpers for the application.
 */

import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Merges tailwind class names and resolves conflicts.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a number as EUR currency.
 */
export function formatCurrency(amount: number | string) {
  const value = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
}

/**
 * Calculates the distance between two coordinates in Kilometers using the Haversine formula.
 */
export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in km
}

function deg2rad(deg: number): number {
  return deg * (Math.PI / 180);
}

/**
 * Normalizes a business name for comparison.
 * - Lowercase
 * - Trim whitespace
 * - Remove extra spaces
 * - Remove common legal suffixes (s.r.l., s.p.a., etc.)
 * - Case-insensitive
 */
export function normalizeBusinessName(name: string): string {
    return name
        .trim()
        .replace(/\s+/g, ' ')  // Normalize multiple spaces to single space
        .replace(/\s*(s\.r\.l\.?|s\.p\.a\.?|s\.n\.c\.?|s\.a\.s\.?|s\.a\.p\.a\.?|s\.d\.f\.?|s\.c\.p\.a\.?|s\.r\.l\.s\.?|s\.p\.?|s\.r\.l|s\.p\.a|s\.n\.c|s\.a\.s|s\.a\.p\.a|s\.d\.f|s\.c\.p\.a|s\.r\.l\.s|srl|spa|snc|sas|sapas|sdf|scpa|srls)\s*$/i, '')
        .toLowerCase();
}

/**
 * Normalizes and cleans a business name for storage
 * - Trims whitespace
 * - Normalizes multiple spaces to single space
 * - Preserves original casing for display
 */
export function cleanBusinessName(name: string): string {
    return name
        .trim()
        .replace(/\s+/g, ' ');  // Normalize multiple spaces to single space
}

/**
 * Escapes HTML special characters for safe insertion into innerHTML/tooltips.
 */
export function escapeHtml(str: string): string {
  return str.replace(/[<>&"']/g, c => ({
    '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;'
  }[c] || c));
}